import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const XR_MODES = [
  {
    id: 'ar', label: 'AR 增强现实', icon: 'ri-glasses-line', color: '#00FFCC',
    title: '增强现实 · 叠加未来',
    subtitle: 'Augmented Reality',
    desc: '将数字信息无缝叠加到真实世界，让每一个物体都成为信息入口。从工业维修到医疗手术，AR正在重塑每一个行业的工作方式。',
    image: 'https://public.readdy.ai/ai/img_res/16c9f76540b509a16147b4b7e089089b.jpg',
    stats: [{ label: '精度', value: '0.1mm' }, { label: '延迟', value: '<5ms' }, { label: '视场角', value: '120°' }, { label: '分辨率', value: '8K/眼' }],
    useCases: [
      { title: '工业AR维修', desc: '技术人员通过AR眼镜实时查看设备内部结构，维修效率提升300%', icon: 'ri-tools-line', detail: '支持远程专家实时指导，将复杂维修流程可视化，减少停机时间85%' },
      { title: '医疗AR手术', desc: '外科医生在手术中实时看到患者器官的3D模型，手术精度达到0.1mm', icon: 'ri-heart-pulse-line', detail: '与MRI/CT数据实时融合，提供血管、神经的精确位置，降低手术风险60%' },
      { title: 'AR导航', desc: '将导航信息直接投影到道路上，驾驶员无需低头看屏幕', icon: 'ri-map-pin-line', detail: '结合实时交通数据，提前预警危险路段，事故率降低40%' },
      { title: 'AR教育', desc: '学生可以与3D历史场景互动，让历史课变成身临其境的体验', icon: 'ri-book-open-line', detail: '学习效率提升3倍，知识留存率从20%提升至75%' },
    ],
  },
  {
    id: 'vr', label: 'VR 虚拟现实', icon: 'ri-vip-crown-line', color: '#A855F7',
    title: '虚拟现实 · 沉浸无界',
    subtitle: 'Virtual Reality',
    desc: '完全沉浸式的虚拟世界体验，让你瞬间置身于任何场景。从太空探索到深海潜水，VR让不可能成为可能，重新定义人类体验的边界。',
    image: 'https://readdy.ai/api/search-image?query=virtual%20reality%20VR%20immersive%20world%20space%20exploration%20purple%20neon%20glowing%20particles%20dark%20background%20cinematic%208k%20ultra%20detailed%20sci-fi&width=1200&height=600&seq=xr-vr-bg-001&orientation=landscape',
    stats: [{ label: '刷新率', value: '240Hz' }, { label: '视场角', value: '220°' }, { label: '延迟', value: '<1ms' }, { label: '分辨率', value: '16K/眼' }],
    useCases: [
      { title: 'VR太空旅行', desc: '足不出户即可体验国际空间站的失重感，探索火星表面', icon: 'ri-rocket-line', detail: '与NASA合作，提供真实太空数据，体验精度达到99.7%' },
      { title: 'VR社交', desc: '在虚拟世界中与全球朋友面对面交流，感受真实的社交临场感', icon: 'ri-group-line', detail: '支持1000人同场互动，表情捕捉精度达到1000个面部特征点' },
      { title: 'VR培训', desc: '飞行员、外科医生在VR中进行高风险操作训练，零风险高效率', icon: 'ri-flight-takeoff-line', detail: '训练效率提升4倍，成本降低90%，已被全球500+机构采用' },
      { title: 'VR治疗', desc: '通过VR暴露疗法治疗恐惧症、PTSD，治愈率提升60%', icon: 'ri-mental-health-line', detail: '已获FDA认证，治疗效果优于传统疗法，无副作用' },
    ],
  },
  {
    id: 'mr', label: 'MR 混合现实', icon: 'ri-magic-line', color: '#FF6B35',
    title: '混合现实 · 虚实共生',
    subtitle: 'Mixed Reality',
    desc: '真实与虚拟的完美融合，数字物体与真实环境实时交互。MR是元宇宙与现实世界的桥梁，开创人机协作新模式，让数字世界真正融入日常生活。',
    image: 'https://readdy.ai/api/search-image?query=mixed%20reality%20MR%20holographic%20objects%20real%20environment%20interaction%20orange%20neon%20glow%20dark%20background%20cinematic%208k%20ultra%20detailed%20sci-fi&width=1200&height=600&seq=xr-mr-bg-001&orientation=landscape',
    stats: [{ label: '空间精度', value: '1mm' }, { label: '追踪速度', value: '1000Hz' }, { label: '延迟', value: '<2ms' }, { label: '识别率', value: '99.9%' }],
    useCases: [
      { title: '全息会议', desc: '与会者的全息投影出现在你的会议室，如同真实在场', icon: 'ri-video-chat-line', detail: '支持全球100个地点同步，全息形象精度达到真人95%' },
      { title: '建筑设计', desc: '建筑师在真实场地上叠加虚拟建筑模型，实时调整设计方案', icon: 'ri-building-2-line', detail: '设计周期缩短60%，客户满意度提升40%，错误率降低80%' },
      { title: '零售体验', desc: '在家中试穿虚拟服装，或将家具放置在真实房间中预览效果', icon: 'ri-shopping-bag-line', detail: '退货率降低35%，转化率提升200%，已被全球1000+品牌采用' },
      { title: '远程协作', desc: '工程师在不同地点共同操作同一个虚拟设备模型', icon: 'ri-team-line', detail: '协作效率提升300%，差旅成本降低70%，已节省数十亿美元' },
    ],
  },
  {
    id: 'spatial', label: '空间计算', icon: 'ri-layout-3-line', color: '#FFD700',
    title: '空间计算 · 无界交互',
    subtitle: 'Spatial Computing',
    desc: '超越屏幕的下一代计算范式，将整个空间变成交互界面。眼动追踪、手势识别和语音控制的完美融合，让计算无处不在，彻底解放双手。',
    image: 'https://readdy.ai/api/search-image?query=spatial%20computing%20hand%20gesture%20control%20holographic%20interface%20floating%20screens%20gold%20yellow%20neon%20dark%20background%20cinematic%208k%20ultra%20detailed%20sci-fi&width=1200&height=600&seq=xr-spatial-bg-001&orientation=landscape',
    stats: [{ label: '手势识别', value: '0.1ms' }, { label: '眼动精度', value: '0.5°' }, { label: '语音识别', value: '99.5%' }, { label: '空间范围', value: '无限' }],
    useCases: [
      { title: '空间办公', desc: '在任何空间中展开无限大的虚拟工作台，多任务处理效率提升500%', icon: 'ri-computer-line', detail: '支持100个虚拟屏幕同时工作，AI自动整理工作流，效率提升5倍' },
      { title: '创意设计', desc: '用双手在空中雕刻3D模型，直觉式创作体验', icon: 'ri-pencil-ruler-2-line', detail: '设计速度提升3倍，创意表达更自然，已被全球顶级设计师采用' },
      { title: '沉浸娱乐', desc: '游戏场景延伸到整个房间，玩家真实移动参与游戏', icon: 'ri-gamepad-line', detail: '全身运动追踪，卡路里消耗是传统游戏的10倍，健康娱乐两不误' },
      { title: '智能家居', desc: '用眼神和手势控制家中所有智能设备，无需任何遥控器', icon: 'ri-home-smile-line', detail: '与10000+智能设备兼容，学习用户习惯，自动化程度达到95%' },
    ],
  },
];

const DEVICES = [
  {
    name: 'META Vision Pro X',
    type: 'MR旗舰头显',
    image: 'https://readdy.ai/api/search-image?query=futuristic%20mixed%20reality%20headset%20sleek%20design%20white%20silver%20premium%20technology%20product%20dark%20background%20glowing&width=500&height=380&seq=xr-dev1-001&orientation=landscape',
    specs: { 重量: '180g', 分辨率: '8K/眼', 视场角: '180°', 续航: '8小时', 处理器: '量子芯片X1', 价格: '¥29,999' },
    color: '#00FFCC',
    badge: '旗舰款',
    desc: '重新定义MR体验的旗舰产品，采用量子芯片X1处理器，实现真正的实时全息渲染。',
  },
  {
    name: 'NEXUS AR Lens',
    type: 'AR智能隐形眼镜',
    image: 'https://readdy.ai/api/search-image?query=smart%20contact%20lens%20augmented%20reality%20technology%20futuristic%20transparent%20lens%20glowing%20circuit%20purple%20dark%20background&width=500&height=380&seq=xr-dev2-001&orientation=landscape',
    specs: { 重量: '0.5g', 续航: '12小时', 分辨率: '4K', 连接: '6G', 传感器: '生物感知', 价格: '¥8,999' },
    color: '#A855F7',
    badge: '革命性',
    desc: '世界首款量产AR隐形眼镜，佩戴后与普通隐形眼镜无异，却能显示完整AR界面。',
  },
  {
    name: 'HoloDesk 3.0',
    type: '全息桌面系统',
    image: 'https://readdy.ai/api/search-image?query=holographic%20desk%20display%20floating%203D%20interface%20futuristic%20office%20technology%20transparent%20projection%20orange%20glow%20dark%20background&width=500&height=380&seq=xr-dev3-001&orientation=landscape',
    specs: { 投影面积: '2m²', 分辨率: '16K', 交互: '手势+语音', 延迟: '<1ms', 亮度: '10000nit', 价格: '¥49,999' },
    color: '#FF6B35',
    badge: '企业级',
    desc: '将整个桌面变成全息投影界面，支持多人协作，是未来办公室的标配。',
  },
  {
    name: 'SpatialGlove Pro',
    type: '触觉反馈手套',
    image: 'https://readdy.ai/api/search-image?query=haptic%20feedback%20glove%20futuristic%20technology%20virtual%20reality%20touch%20sensation%20smart%20glove%20gold%20yellow%20glow%20dark%20background&width=500&height=380&seq=xr-dev4-001&orientation=landscape',
    specs: { 触点数: '1000+', 精度: '0.1mm', 延迟: '<2ms', 兼容: '全平台', 材质: '纳米纤维', 价格: '¥5,999' },
    color: '#FFD700',
    badge: '触觉革命',
    desc: '1000个触觉反馈点，让你在虚拟世界中真实感受到物体的质感、温度和重量。',
  },
];

export default function XRPage() {
  const navigate = useNavigate();
  const [activeMode, setActiveMode] = useState(XR_MODES[0]);
  const [selectedDevice, setSelectedDevice] = useState<typeof DEVICES[0] | null>(null);
  const [selectedCase, setSelectedCase] = useState<typeof XR_MODES[0]['useCases'][0] | null>(null);
  const [animIn, setAnimIn] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'META66 XR沉浸体验 | AR增强现实·VR虚拟现实·MR混合现实·空间计算';
    setTimeout(() => setAnimIn(true), 100);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const lines: { x1: number; y1: number; x2: number; y2: number; alpha: number; speed: number; color: string }[] = [];
    const colors = [activeMode.color + '60', activeMode.color + '30'];
    for (let i = 0; i < 20; i++) {
      lines.push({
        x1: Math.random() * canvas.width, y1: Math.random() * canvas.height,
        x2: Math.random() * canvas.width, y2: Math.random() * canvas.height,
        alpha: Math.random() * 0.3 + 0.05, speed: Math.random() * 0.5 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      lines.forEach(l => {
        ctx.beginPath();
        ctx.moveTo(l.x1, l.y1);
        ctx.lineTo(l.x2, l.y2);
        ctx.strokeStyle = l.color;
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = l.alpha;
        ctx.stroke();
        ctx.globalAlpha = 1;
        l.x2 += l.speed; l.y2 += l.speed * 0.3;
        if (l.x2 > canvas.width) { l.x2 = 0; l.x1 = Math.random() * canvas.width; }
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, [activeMode.color]);

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: '#020810', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      {/* Fixed BG */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(${activeMode.color}20 1px, transparent 1px), linear-gradient(90deg, ${activeMode.color}20 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
          transition: 'all 0.8s ease',
        }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 0%, ${activeMode.color}08 0%, transparent 60%)`, transition: 'all 0.8s ease' }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${activeMode.color}25`, backdropFilter: 'blur(20px)', transition: 'border-color 0.5s ease' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap hover:scale-105" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <i className="ri-arrow-left-line" />
            <span className="text-sm">返回首页</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${activeMode.color}20`, border: `1.5px solid ${activeMode.color}50`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%', transition: 'all 0.5s ease' }}>
              <i className="ri-glasses-2-line text-white text-sm" />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>XR EXPERIENCE</span>
          </div>
          <div className="text-sm" style={{ color: `${activeMode.color}80`, fontFamily: 'Caveat, cursive', transition: 'color 0.5s ease' }}>META66 · 感知未来</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 h-[520px] overflow-hidden">
        <img
          src={activeMode.image}
          alt={activeMode.title}
          className="w-full h-full object-cover object-top transition-all duration-1000"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.3) 0%, rgba(2,8,16,0.6) 60%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 30% 50%, ${activeMode.color}12 0%, transparent 60%)` }} />

        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 0.9s ease' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5" style={{ background: `${activeMode.color}15`, border: `1.5px solid ${activeMode.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: activeMode.color }} />
              <span className="text-sm font-bold" style={{ color: activeMode.color, fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>{activeMode.subtitle}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${activeMode.color}, white)` }}>XR</span>
              <br />
              <span className="text-white text-4xl md:text-5xl">{activeMode.title}</span>
            </h1>
            <p className="text-white/55 text-lg max-w-2xl leading-relaxed">{activeMode.desc}</p>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-4 mt-6">
              {activeMode.stats.map(s => (
                <div key={s.label} className="px-4 py-2" style={{ background: 'rgba(2,8,16,0.7)', border: `1px solid ${activeMode.color}30`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px', backdropFilter: 'blur(10px)' }}>
                  <p className="font-black text-lg" style={{ color: activeMode.color, fontFamily: 'Orbitron, sans-serif' }}>{s.value}</p>
                  <p className="text-white/40 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mode Tabs on Hero Bottom */}
        <div className="absolute bottom-0 left-0 right-0 flex">
          {XR_MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode)}
              className="flex-1 py-4 text-sm font-bold transition-all duration-500 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              style={{
                background: activeMode.id === mode.id ? `${mode.color}25` : 'rgba(2,8,16,0.8)',
                color: activeMode.id === mode.id ? mode.color : 'rgba(255,255,255,0.35)',
                borderTop: `2px solid ${activeMode.id === mode.id ? mode.color : 'transparent'}`,
                backdropFilter: 'blur(10px)',
                fontFamily: 'Caveat, cursive',
                fontSize: '0.95rem',
              }}
            >
              <i className={mode.icon} />
              <span className="hidden sm:inline">{mode.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-16">

        {/* Use Cases */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${activeMode.color}40)` }} />
            <h2 className="text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span style={{ color: activeMode.color }}>{activeMode.subtitle}</span> 应用场景
            </h2>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${activeMode.color}40, transparent)` }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {activeMode.useCases.map((uc, i) => (
              <div
                key={uc.title}
                className="group p-6 rounded-2xl cursor-pointer transition-all duration-400 hover:-translate-y-3"
                style={{
                  background: `${activeMode.color}06`,
                  border: `1.5px solid ${activeMode.color}20`,
                  borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
                  opacity: animIn ? 1 : 0,
                  transform: animIn ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease ${i * 100}ms`,
                }}
                onClick={() => navigate(`/xr/${activeMode.id}`)}
              >
                <div
                  className="w-14 h-14 flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                  style={{ background: `${activeMode.color}15`, border: `2px solid ${activeMode.color}35`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}
                >
                  <i className={`${uc.icon} text-2xl`} style={{ color: activeMode.color }} />
                </div>
                <h3 className="text-white font-bold text-base mb-2">{uc.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed mb-4">{uc.desc}</p>
                <div className="flex items-center gap-1 text-xs font-bold" style={{ color: activeMode.color }}>
                  <span>查看详情</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Devices */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${activeMode.color}40)` }} />
            <h2 className="text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: 'Orbitron, sans-serif' }}>硬件生态</h2>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${activeMode.color}40, transparent)` }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {DEVICES.map((device, i) => (
              <div
                key={device.name}
                className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-3"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: `1.5px solid ${device.color}25`,
                  borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
                  opacity: animIn ? 1 : 0,
                  transform: animIn ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease ${i * 100}ms`,
                }}
                onClick={() => setSelectedDevice(device)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={device.image} alt={device.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, rgba(2,8,16,0.95) 100%)` }} />
                  <div
                    className="absolute top-3 left-3 px-2.5 py-1 text-xs font-black"
                    style={{ background: device.color, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', color: '#000', fontFamily: 'Caveat, cursive' }}
                  >
                    {device.badge}
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-white/35 text-xs mb-1">{device.type}</p>
                  <h3 className="text-white font-bold text-base mb-2">{device.name}</h3>
                  <p className="text-white/45 text-xs leading-relaxed mb-4">{device.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-black text-xl" style={{ color: device.color, fontFamily: 'Orbitron, sans-serif' }}>{device.specs['价格']}</span>
                    <span className="text-xs px-3 py-1" style={{ background: `${device.color}15`, color: device.color, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>查看详情</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Deep Dive Buttons for 4 XR Modes */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${activeMode.color}40)` }} />
            <h2 className="text-2xl font-black text-white whitespace-nowrap" style={{ fontFamily: 'Orbitron, sans-serif' }}>深度解析 · 四大技术</h2>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${activeMode.color}40, transparent)` }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {XR_MODES.map((mode) => (
              <button
                key={mode.id}
                onClick={() => navigate(`/xr/${mode.id}`)}
                className="group relative p-5 rounded-2xl text-left transition-all duration-300 cursor-pointer hover:-translate-y-2"
                style={{
                  background: activeMode.id === mode.id ? `${mode.color}15` : 'rgba(255,255,255,0.03)',
                  border: `1.5px solid ${activeMode.id === mode.id ? mode.color + '50' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ background: `${mode.color}15`, border: `1.5px solid ${mode.color}35`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                  <i className={`${mode.icon} text-lg`} style={{ color: mode.color }} />
                </div>
                <p className="text-white font-bold text-sm mb-1">{mode.label}</p>
                <p className="text-white/40 text-xs mb-3">{mode.subtitle}</p>
                <div className="flex items-center gap-1 text-xs font-bold" style={{ color: mode.color }}>
                  <span>深度解析</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* XR Stats */}
        <div
          className="p-10 rounded-3xl"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: `1px solid ${activeMode.color}20`,
            borderRadius: '28px 20px 32px 24px / 20px 32px 24px 28px',
          }}
        >
          <h2 className="text-3xl font-black text-white mb-10 text-center" style={{ fontFamily: 'Orbitron, sans-serif' }}>XR 行业数据</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: '全球XR用户', value: '8.5亿', sub: '2025年预测', color: '#A855F7' },
              { label: '市场规模', value: '$1.2万亿', sub: '2030年预测', color: '#00FFCC' },
              { label: '内容创作者', value: '2000万+', sub: '元宇宙生态', color: '#FF6B35' },
              { label: '企业采用率', value: '67%', sub: '财富500强', color: '#FFD700' },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl font-black mb-2" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</div>
                <div className="text-white/70 font-bold mb-1">{stat.label}</div>
                <div className="text-white/30 text-sm">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Device Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }} onClick={() => setSelectedDevice(null)}>
          <div className="w-full max-w-lg" style={{ background: '#060C18', border: `2px solid ${selectedDevice.color}40`, borderRadius: '24px 18px 28px 20px / 18px 28px 20px 24px' }} onClick={e => e.stopPropagation()}>
            <div className="relative h-56 overflow-hidden" style={{ borderRadius: '22px 16px 0 0' }}>
              <img src={selectedDevice.image} alt={selectedDevice.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(6,12,24,0.95) 100%)' }} />
              <button onClick={() => setSelectedDevice(null)} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center cursor-pointer" style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', color: 'rgba(255,255,255,0.7)' }}>
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-white/35 text-xs mb-1">{selectedDevice.type}</p>
              <h2 className="text-white font-black text-2xl mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>{selectedDevice.name}</h2>
              <p className="text-white/50 text-sm mb-5">{selectedDevice.desc}</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {Object.entries(selectedDevice.specs).filter(([k]) => k !== '价格').map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl" style={{ background: `${selectedDevice.color}08`, border: `1px solid ${selectedDevice.color}20` }}>
                    <p className="text-white/35 text-xs mb-1">{k}</p>
                    <p className="text-white font-bold text-sm">{v}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="font-black text-3xl" style={{ color: selectedDevice.color, fontFamily: 'Orbitron, sans-serif' }}>{selectedDevice.specs['价格']}</span>
                <button className="px-6 py-3 font-black text-black cursor-pointer hover:scale-105 transition-all whitespace-nowrap" style={{ background: selectedDevice.color, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>立即预订</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Case Modal */}
      {selectedCase && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }} onClick={() => setSelectedCase(null)}>
          <div className="w-full max-w-md p-8" style={{ background: '#060C18', border: `2px solid ${activeMode.color}40`, borderRadius: '24px 18px 28px 20px / 18px 28px 20px 24px' }} onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 flex items-center justify-center mb-5" style={{ background: `${activeMode.color}15`, border: `2px solid ${activeMode.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
              <i className={`${selectedCase.icon} text-3xl`} style={{ color: activeMode.color }} />
            </div>
            <h2 className="text-white font-black text-2xl mb-3">{selectedCase.title}</h2>
            <p className="text-white/55 text-sm leading-relaxed mb-4">{selectedCase.desc}</p>
            <div className="p-4 rounded-xl mb-6" style={{ background: `${activeMode.color}08`, border: `1px solid ${activeMode.color}20` }}>
              <p className="text-white/35 text-xs font-bold mb-2">深度解析</p>
              <p className="text-white/65 text-sm leading-relaxed">{selectedCase.detail}</p>
            </div>
            <button onClick={() => setSelectedCase(null)} className="w-full py-3 font-black text-black cursor-pointer whitespace-nowrap" style={{ background: activeMode.color, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>了解更多</button>
          </div>
        </div>
      )}
    </div>
  );
}
